import { useDidUpdate, useDisclosure } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useStrategyStore } from '../../store/strategy-store/strategy-store';
import { setActivePanel, setSelectedInitiative } from '../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../store/strategy-store/strategy-store.type';
import { baseWhite } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Icon } from '../../ui-library/icon/icon';
import { toast } from '../notifications';
import { getInitiativesConfig } from './initiatives.config';
import { StrategyEntityModal } from './strategy-entity-modal/strategy-entity-modal';
import { StrategyView } from './strategy-view';
import {
  useCreateGoalMutation,
  useCreateInitiativeMutation,
  useCreateOutcomeMutation,
  useStrategyData,
} from './strategy.hooks';
import { StrategyEntityType } from './strategy.type';

export const Strategy = () => {
  const i18n = useTranslation();
  const initiativesConfig = useMemo(() => getInitiativesConfig(i18n.t), [i18n.t]);
  const { initiativesQuery, kpiOverviewDataQuery } = useStrategyData();

  const portfolio = useGlobalStore((state) => state.portfolio);
  const activePanel = useStrategyStore((state) => state.activePanel);

  const [opened, { open, close }] = useDisclosure(false);
  const [modalEntityType, setModalEntityType] = useState<StrategyEntityType>(StrategyEntityType.Initiative);

  useEffect(() => {
    return () => {
      setActivePanel(Panel.Filters);
    };
  }, []);

  useDidUpdate(() => {
    if (portfolio) {
      setActivePanel(Panel.Filters);
    }
  }, [portfolio]);

  const createInitiativeMutation = useCreateInitiativeMutation(portfolio, {
    onSuccess: (data) => {
      initiativesQuery.refetch();
      kpiOverviewDataQuery.refetch();
      close();

      if (data.message) {
        toast.warning(data.message);
      } else {
        toast.success(i18n.t('strategy.initiatives.created_success', { name: data.name }));
      }
    },
    onError: (error) => {
      toast.error(
        i18n.t('strategy.initiatives.create_error', {
          error: error instanceof Error ? error.message : i18n.t('strategy.initiatives.unknown_error'),
        }),
      );
    },
  });

  const createOutcomeMutation = useCreateOutcomeMutation({
    onSuccess: (data) => {
      initiativesQuery.refetch();
      kpiOverviewDataQuery.refetch();
      close();
      toast.success(`Outcome "${data.name}" created successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to create outcome: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const createGoalMutation = useCreateGoalMutation({
    onSuccess: (data) => {
      initiativesQuery.refetch();
      kpiOverviewDataQuery.refetch();
      close();
      toast.success(`Goal "${data.name}" created successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to create goal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  /*
   * @param show - Whether to show the filters
   * @description Handles the show state of the filters
   */
  function handleSetShow(show: boolean): void {
    setActivePanel(show ? Panel.Filters : null);
    if (show) {
      setSelectedInitiative(null);
    }
  }

  // Create the Add New button - this is the Initiatives page
  const addInitiativeButton = (
    <Button
      data-bf="strategy-btn-add-initiative"
      leftSection={<Icon name="add_circle" size={16} color={baseWhite} />}
      variant="primary"
      radius="xl"
      onClick={() => {
        setModalEntityType(StrategyEntityType.Initiative);
        open();
      }}
      size="sm"
    >
      Add New
    </Button>
  );

  return (
    <StrategyView
      config={{
        ...initiativesConfig,
        renderers: {
          renderAddButton: () => addInitiativeButton,
        },
      }}
      customShowSideSection={activePanel === Panel.Side || activePanel === Panel.OtherInitiatives}
      customFilterShow={activePanel === Panel.Filters}
      customSetFilterShow={handleSetShow}
    >
      <StrategyEntityModal
        mode="create"
        entityType={modalEntityType}
        opened={opened}
        onClose={close}
        onSubmit={
          modalEntityType === 'initiative'
            ? createInitiativeMutation.mutateAsync
            : modalEntityType === 'outcome'
              ? createOutcomeMutation.mutateAsync
              : createGoalMutation.mutateAsync
        }
        isSubmitting={
          modalEntityType === 'initiative'
            ? createInitiativeMutation.isPending
            : modalEntityType === 'outcome'
              ? createOutcomeMutation.isPending
              : createGoalMutation.isPending
        }
      />
    </StrategyView>
  );
};
